import React, { useCallback, useEffect, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";

/**
 * Phase QCM-EXT followup — lightweight collapsible section wrapper.
 *
 * Why a custom wrapper instead of @radix-ui/react-collapsible?
 *  - Radix Collapsible is NOT in package.json today; pulling it in for
 *    two sections is overkill. Plain CSS height transition is fine.
 *
 * Props:
 *  - title:           string section title
 *  - storageKey:      localStorage key used to persist collapsed state
 *  - defaultCollapsed:bool — initial state if no localStorage entry yet
 *  - headerExtras:    optional ReactNode rendered on the right of the
 *                     header, BEFORE the chevron. Stays visible even
 *                     when the section is collapsed — use this slot
 *                     for primary actions (e.g. "Re-verify with FMCSA").
 *                     Wrap action buttons inside <div onClick=stopProp>
 *                     OR rely on the built-in behavior: clicks inside
 *                     headerExtras DO NOT propagate to the toggle.
 *  - testIdPrefix:    string for data-testid attributes
 *
 * Click target: the chevron + title area. headerExtras is excluded
 * via `e.stopPropagation()` so primary actions don't accidentally
 * toggle the section.
 */
export function CollapsibleSection({
    title,
    storageKey,
    defaultCollapsed = false,
    headerExtras = null,
    testIdPrefix = "collapsible-section",
    children,
}) {
    const [collapsed, setCollapsed] = useState(() => {
        if (typeof window === "undefined") return defaultCollapsed;
        try {
            const v = window.localStorage.getItem(storageKey);
            if (v === null) return defaultCollapsed;
            return v === "1";
        } catch (e) {
            // Safari private mode, embedded webviews, etc. — fall back
            // to the in-memory default rather than crashing.
            return defaultCollapsed;
        }
    });

    useEffect(() => {
        if (!storageKey) return;
        try {
            window.localStorage.setItem(storageKey, collapsed ? "1" : "0");
        } catch (e) {
            // Same swallow as above — persistence is best-effort.
        }
    }, [collapsed, storageKey]);

    const toggle = useCallback(() => setCollapsed((v) => !v), []);

    return (
        <section
            className="rounded-md border border-slate-200 bg-white"
            data-testid={`${testIdPrefix}-section`}
            data-collapsed={collapsed ? "true" : "false"}
        >
            <div
                role="button"
                tabIndex={0}
                onClick={toggle}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(); }
                }}
                className="flex items-center justify-between gap-3 px-4 py-3 cursor-pointer select-none hover:bg-slate-50 rounded-t-md"
                data-testid={`${testIdPrefix}-toggle`}
                aria-expanded={!collapsed}
            >
                <div className="flex items-center gap-2 min-w-0">
                    {collapsed ? (
                        <ChevronRight size={16} className="text-slate-500 shrink-0" />
                    ) : (
                        <ChevronDown size={16} className="text-slate-500 shrink-0" />
                    )}
                    <h3
                        className="text-xs font-medium tracking-[0.18em] uppercase text-slate-600 truncate"
                        data-testid={`${testIdPrefix}-title`}
                    >
                        {title}
                    </h3>
                </div>
                {headerExtras && (
                    <div
                        // Stop propagation so buttons inside `headerExtras`
                        // never trigger the section toggle. Use `onClick` on
                        // the wrapper because synthetic events bubble.
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        className="flex items-center gap-2 shrink-0"
                        data-testid={`${testIdPrefix}-actions`}
                    >
                        {headerExtras}
                    </div>
                )}
            </div>
            {!collapsed && (
                <div className="px-4 pb-4 pt-1" data-testid={`${testIdPrefix}-content`}>
                    {children}
                </div>
            )}
        </section>
    );
}

export default CollapsibleSection;
